<script setup lang="ts">
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

import { ref, onMounted, computed } from 'vue'
import { useLogger } from 'vue-logger-plugin'

import type { IAoeCart } from '../../types/CartTypes'
import type { IProduct } from '../../types/CategoryTypes'
import type { IProductAttributes } from '../../types/SkuTypes'

import {  DropDownList, type DropDownListChangeEvent } from "@progress/kendo-vue-dropdowns";

import { Button as KButton } from '@progress/kendo-vue-buttons'

import { GetCategoriesFromLocalStorageOrApi, GetProductsByCategoryAndOrderType } from '../../services/CategoryService'
import { GetFullProductImageDictionary } from '../../services/SkuService.ts'
import { SetIsLoading } from '../../services/AoeHelpers.ts'
import { handleError } from '../../services/ErrorService.ts'
import { AddItemToCart } from '../../services/CartService'
import { GetOrderTypeCountry } from '../../services/CommonService'
import { GetAvailableQty } from '../../services/InventoryService'
import { FormatCurrency } from '../../services/currencyFormatter'
import Swal from "sweetalert2"

import PricePerUnitWeight from '../PricePerUnitWeight.vue'

const logger = useLogger()

const emit = defineEmits(["update-cart", "update-selected-category"]);

const selectedCategory = ref<CatItem>({ text: '', id: '' });

interface CatItem {
  text: string
  id: string
}

const props = defineProps<{
  cart: IAoeCart | null
  showheader: boolean
  ordertypeid: string
  entryselectedcategory?: string
}>()

const catDropdownData = ref<CatItem[]>([])
const productImageUrls = ref<Record<string, string>>({})
const productAttributesByProductCode = ref<Record<string, IProductAttributes | null>>({})
const gridItems = ref<IProduct[]>([])
const showGrid = ref<boolean>(false)
const orderTypeId = ref<string>('')
const noResultsFound = ref<boolean>(false)

// Paging state
const skip = ref<number>(0)
const pageSize = ref<number>(20) // You can adjust this value as needed

interface PagerProps {
  total: number;
  pageSize: number;
  skip: number;
  buttonCount?: number;
  pageChange: (params: { skip: number; take: number }) => void;
}

import { h } from 'vue';

function customPagerRender(pagerProps: PagerProps) {
  const total = pagerProps.total;
  const pageSize = pagerProps.pageSize;
  const skip = pagerProps.skip;
  const buttonCount = pagerProps.buttonCount || 5;
  const pageCount = Math.ceil(total / pageSize);
  const currentPage = Math.floor(skip / pageSize) + 1;

  const pages = [];
  let start = Math.max(1, currentPage - Math.floor(buttonCount / 2));
  const end = Math.min(pageCount, start + buttonCount - 1);
  if (end - start < buttonCount - 1) {
    start = Math.max(1, end - buttonCount + 1);
  }

  // First page
  if (start > 1) {
    pages.push(h('button', {
      class: ['aoe-pager-btn', { 'k-selected': currentPage === 1 }],
      onClick: () => pagerProps.pageChange({ skip: 0, take: pageSize }),
      type: 'button',
      'aria-label': 'Go to page 1',
    }, '1'));
    if (start > 2) {
      pages.push(h('span', { class: 'aoe-pager-ellipsis' }, '...'));
    }
  }

  // Page range
  for (let i = start; i <= end; ++i) {
    pages.push(h('button', {
      class: ['aoe-pager-btn', { 'k-selected': i === currentPage }],
      onClick: () => pagerProps.pageChange({ skip: (i - 1) * pageSize, take: pageSize }),
      type: 'button',
      disabled: i === currentPage,
      'aria-label': `Go to page ${i}`,
    }, String(i)));
  }

  // Last page
  if (end < pageCount) {
    if (end < pageCount - 1) {
      pages.push(h('span', { class: 'aoe-pager-ellipsis' }, '...'));
    }
    pages.push(h('button', {
      class: ['aoe-pager-btn', { 'k-selected': currentPage === pageCount }],
      onClick: () => pagerProps.pageChange({ skip: (pageCount - 1) * pageSize, take: pageSize }),
      type: 'button',
      'aria-label': `Go to page ${pageCount}`,
    }, String(pageCount)));
  }

  return h('div', { class: 'aoe-pager-bar' }, pages);
}


function onPageChange(e: { skip: number }) {
  skip.value = e.skip;
}

const pagedGridItems = computed(() => {
  return gridItems.value.slice(skip.value, skip.value + pageSize.value);
});

onMounted(async () => {
  logger.debug('onMounted', 'AoeCatalogEntry')

  // Set orderTypeId from prop if provided
  if (props.ordertypeid) {
    orderTypeId.value = props.ordertypeid
  }

  orderTypeId.value = await GetOrderTypeCountry(orderTypeId.value)

  GetCategoriesFromLocalStorageOrApi({orderTypeId: orderTypeId.value}).then((categories) => {
    logger.debug('GetCategoriesFromLocalStorageOrApi', categories)
    if (categories != null)
    {
      for (const cats of categories)
      {
        const catItem = categories.filter(x => x.category.categoryCode == cats.category.categoryCodeParent)
        if (catItem.length == 0)
        {
          catDropdownData.value.push({ text: cats.category.categoryName, id: cats.category.categoryCode })

          if (cats.categoryChildren != null)
          {
            for (const catChild of cats.categoryChildren)
            {
              catDropdownData.value.push({ text: t('labels.categoryPrefix') + catChild.categoryName, id: catChild.categoryCode })
            }
          }
        }
      };

      if (props.entryselectedcategory != undefined && props.entryselectedcategory.length > 0)
      {
        const selected : CatItem = catDropdownData.value.filter(x => x.id == props.entryselectedcategory)[0]
        selectedCategory.value = { text: selected.text, id: props.entryselectedcategory }
      }
      else
      {
        selectedCategory.value = catDropdownData.value[0]
      }
      onCategoryChange(null);
    }
  }).catch((error) => {
    logger.error('GetCategoriesFromLocalStorageOrApi', error)
  })
})

async function addToCartClickedHandler(event: MouseEvent, skuCode: string) {
  logger.debug('addToCartClickedHandler', event)

  if (!props.cart || !skuCode) {
    return;
  }

  const _product = gridItems.value.find(p => p.skus && p.skus.some(s => s.skuCode === skuCode))
  const _sku = _product?.skus?.find(s => s.skuCode === skuCode)

  if (!props.cart.isHostCart && _sku?.onlyAvailableForHost) {
    await Swal.fire({
      title: t('alerts.HostOnlyItemTitle'),
      text: t('alerts.HostOnlyItemMessage'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay')
    })
    return
  }

  if (_sku?.onlyAvailableForDiscount) {
    await Swal.fire({
      title: t('alerts.DiscountOnlyItemTitle'),
      text: t('alerts.DiscountOnlyItemMessage'),
      icon: 'error',
      confirmButtonText: t('buttons.Okay')
    })
    return
  }

  SetIsLoading(true)

  try {
    // Check stock availability before adding to cart
    const availableQty = await GetAvailableQty(orderTypeId.value, skuCode);

    if (availableQty < 1) {
      // Show error for unavailable item
      await Swal.fire({
        title: t('alerts.AddToCartError'),
        text: t('alerts.NoQuantityAvailable'),
        icon: 'error',
        confirmButtonText: t('buttons.Okay')
      });

      SetIsLoading(false);
      return;
    }

    // Item is available, proceed with adding to cart
    await AddItemToCart(
      props.cart.aoeCartIdToken,
      null,
      null,
      orderTypeId.value,
      skuCode,
      1
    );
  } catch (error) {
    await handleError(error, t);
  } finally {
    SetIsLoading(false);
    emit("update-cart", event);
  }
}

function onCategoryChange(event: DropDownListChangeEvent | null) {
  logger.debug('onCategoryChange', event)

  if (event != null)
  {
    selectedCategory.value = event.value
    emit('update-selected-category', selectedCategory.value.id)
  }

  GetProductsByCategoryAndOrderType(
    { orderTypeId: orderTypeId.value,
      categoryCode: selectedCategory.value.id,
      includeInventoryLevels: true,
      beeNumber: props.cart == null ? null : props.cart.beeNumberCustomer
    }).then((products) => {
    logger.debug('GetProductsByCategoryAndOrderType', products)
    if (products != null)
    {
        showGrid.value = true
        const allProducts = products.item.products
        gridItems.value = allProducts.filter(p =>
          !p.skus || p.skus.length === 0 ||
          (!p.skus[0].onlyAvailableForDiscount &&
            (props.cart?.isHostCart || !p.skus[0].onlyAvailableForHost))
        )
        noResultsFound.value = gridItems.value.length === 0;
        skip.value = 0; // Reset to first page on category change
        GetProductImageUrls(products.item.products).then((pIUrls) => {
          logger.debug('GetProductImageUrls', pIUrls)
          productImageUrls.value = pIUrls
        })

        const next: Record<string, IProductAttributes | null> = {}
        for (const prod of products.item.products ?? []) {
          const raw = prod?.attributes?.PricePerUnitWeight
          const parsed = raw != null ? Number(raw) : NaN
          if (Number.isFinite(parsed) && parsed > 0) {
            next[prod.productCode] = { CatalogImage: '', PricePerUnitWeight: parsed }
          } else {
            next[prod.productCode] = null
          }
        }
        productAttributesByProductCode.value = next
    } else {
        noResultsFound.value = true;
    }
  }).catch((error) => {
    logger.error('GetProductsByCategoryAndOrderType', error)
  })
}

function GetFullProductImageUrlFromStringDictionary(productCode: string) {
  logger.debug('GetFullProductImageUrlFromStringDictionary', productCode, productImageUrls.value)
  const productImageUrl = productImageUrls.value[productCode]
  if (productImageUrl) return productImageUrl
  return '#'
}

async function GetProductImageUrls(products: IProduct[]) : Promise<Record<string, string>>
{
  const uniqueProductCodes = Array<string>()
  for(const prod of products) {
    if (!uniqueProductCodes.includes(prod.productCode)) {
      uniqueProductCodes.push(prod.productCode)
    }
  }
  const pIUrls = await GetFullProductImageDictionary(uniqueProductCodes)
  return pIUrls
}

function formatCurrency(value : number) {
  // Use the centralized FormatCurrency function from currencyFormatter.ts
  // Pass the currencyId from the cart, or null if not available
  const currencyId = props.cart?.currencyId || null
  return FormatCurrency(value, currencyId)
}

function getYourPrice(prod: IProduct): number | null {
  if (!prod.skus || prod.skus.length === 0) return null
  const sku = prod.skus[0]
  const buyingDiscount = sku.buyingDiscount != undefined ? sku.buyingDiscount : 0
  return sku.retailPrice - buyingDiscount
}

function isOutOfStock(prod: IProduct): boolean {
  if (!prod.skus || prod.skus.length === 0) return true
  const sku = prod.skus[0]
  const inventoryLevel = sku.inventoryLevel
  if (!inventoryLevel) return true
  // If OutOfStockVisible is true, product is available for purchase
  if (sku.outOfStockVisible === true) return false
  return inventoryLevel.inventoryLevel === 'OutOfStock' || inventoryLevel.inventoryLevel === 'StopSell'
}
</script>

<template>
  <div class="aoe-category-entry">
    <div v-if="props.showheader" class="row party-order-entry-section-header-row">
      <div class="col-12 party-order-entry-section-header-div category-entry-section-header">
        {{ t('partyOrderEntry.CategoryEntryHeader') }}
      </div>
    </div>
    <div class="row choose-category-row mt-3">
      <div class="col-12 col-md-2">
        {{ t('partyOrderEntry.Category') }}
      </div>
      <div class="col-12 col-md-10">
        <DropDownList
          :data-items="catDropdownData"
          :text-field="'text'"
          :data-item-key="'id'"
          :value="selectedCategory"
          @change="onCategoryChange($event)"
        >
        </DropDownList>
      </div>
    </div>
    <div v-if="noResultsFound" class="row mt-3">
      <div class="col-12 text-center text-danger">
        {{ t('partyOrderEntry.NoResultsFound') }}
      </div>
    </div>
    <div v-else class="row mt-3 catalog-row d-flex flex-wrap">
      <div
        class="mt-3 col-6 col-md-3 d-flex catalog-product-card"
        v-for="prod in pagedGridItems"
        :key="prod.productCode">
        <img
          :src="GetFullProductImageUrlFromStringDictionary(prod.productCode)"
          class="catalog-product-image"
          :alt="t('cart.ProductImageAlt')"
        />
        <div class="row mt-3">
          <div class="col-12 catalog-product-name-div text-center">
            {{ prod.productName }}
          </div>
        </div>
        <div class="mt-1 col-12 catalog-product-your-price-div text-center">
          {{ t('partyOrderEntry.CatalogYourPrice') }}
          {{ prod.skus.length > 0 ? formatCurrency(prod.skus[0].retailPrice - (prod.skus[0].buyingDiscount != undefined ? prod.skus[0].buyingDiscount : 0)) : formatCurrency(0) }}
        </div>
        <div class="mt-1 col-12 catalog-product-price-div text-center">
          {{ prod.skus.length > 0 && prod.skus[0].pricingNotFound ? t('partyOrderEntry.MissingPricing') : '' }}
          {{ t('partyOrderEntry.CatalogRetailPrice') }}
          {{ prod.skus.length > 0 ? formatCurrency(prod.skus[0].retailPrice) : '' }}
        </div>
        <div class="mt-1 col-12 text-center">
          <PricePerUnitWeight
            :RetailPrice="prod.skus.length > 0 ? prod.skus[0].retailPrice : null"
            :SalePrice="getYourPrice(prod)"
            :IProductAttributes="productAttributesByProductCode[prod.productCode] ?? null"
          />
        </div>
        <div class="mt-auto pt-3 col-12 catalog-product-add-button-div text-center">
          <KButton
            @click="addToCartClickedHandler($event, prod.skus[0].skuCode)"
            class="baseBtn primaryBtn catalog-add-to-cart-button"
            :rounded="'large'"
            :theme-color="'checkoutContinue'"
            :disabled="isOutOfStock(prod)">
            {{ isOutOfStock(prod) ? t('partyOrderEntry.ItemNotAvailable') : t('labels.AddToCart') }}
       </KButton>
        </div>
      </div>
    </div>
    <div v-if="!noResultsFound && gridItems.length > pageSize" class="row mt-3">
      <div class="col-12 d-flex justify-content-center">
        <div>
  <!-- Custom pager bar: always shows page numbers and ellipses, never a dropdown -->
  <component
    :is="customPagerRender"
    :total="gridItems.length"
    :pageSize="pageSize"
    :skip="skip"
    :buttonCount="5"
    :pageChange="(params) => onPageChange(params)"
  />
</div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">

.add-to-cart-button-div {
  text-align: right;
}

.inventory-stop-sell
{
  color: red;
}

.inventory-available
{
  color: green;
}

.inventory-low-stock
{
  color: yellow;
}

.inventory-out-of-stock
{
  color: orange;
}

.product-grid-stock-indicator,
.stock-indicator-key-div
{
  text-align: center !important;
}

.product-grid-ps-header .k-column-title
{
  margin-left: auto;
  margin-right: auto;
}

.catalog-product-card {
  flex-direction: column;
}

.catalog-product-name-div {
  @include typography-body;
  @include font-weight-semibold;
}

.catalog-product-your-price-div {
  @include font-weight-semibold;
}

.product-grid-stock-indicator .k-svg-icon,
.product-grid-stock-indicator .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.product-grid-stock-indicator .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg,
.stock-indicator-key-div .k-svg-icon,
.stock-indicator-key-div .k-button-md.k-icon-button .k-button-icon.k-svg-icon>svg,
.stock-indicator-key-div .k-button-md.k-icon-button .k-button-icon.k-icon-md>svg
{
    width: 40px;
    height: 40px;
}

.stock-indicator-key-row
{
  border: 1px solid var(--kendo-color-border);
}

.aoe-pager-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin: 1rem 0;
  flex-wrap: wrap;
}

.aoe-pager-btn {
  background: var(--vt-c-white-soft);
  border: 1px solid #bdbdbd;
  color: #333;
  border-radius: 0.5rem;
  min-width: 2.2rem;
  height: 2.2rem;
  @include typography-body;
  @include font-weight-medium;
  padding: 0 0.8rem;
  cursor: pointer;
  transition: background 0.2s, color 0.2s, box-shadow 0.2s;
  outline: none;
  margin: 0 0.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  box-sizing: border-box;
}

.aoe-pager-btn.k-selected, .aoe-pager-btn:disabled {
  background: #e0e0e0;
  color: #222;
  border: 1px solid #bdbdbd;
  cursor: default;
  box-shadow: 0 2px 8px rgba(#bdbdbd, 0.15);
}

.aoe-pager-btn:not(.k-selected):hover:not(:disabled) {
  background: #f0f0f0;
  color: #111;
}

.aoe-pager-ellipsis {
  @include typography-h4;
  color: #aaa;
  padding: 0 0.3rem;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
}

.catalog-add-to-cart-button:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

@media (max-width: 768px) {
  .aoe-category-entry {
    .catalog-product-add-button-div {
      .baseBtn.catalog-add-to-cart-button {
        min-width: unset;
      }
    }

    /* hack to adjust the position of the 2nd column's add to cart buttons on mobile */
    .catalog-product-card:nth-child(2n) .catalog-add-to-cart-button {
      margin-left: calc(-1 * #{$spacing-2});
    }
  }
}

</style>
